import React, { useState, useEffect } from 'react';
import {
  Box,
  DialogContent,
  Button,
  DialogActions,
  Typography,
  CircularProgress,
} from '@mui/material';
import BootstrapDialog, {
  BootstrapDialogTitle,
} from '../../common/dialog/BootstrapDialog';
import * as projectApi from '../../../lib/api/project';

// PDF 미리보기/다운로드 (Excel → PDF, 서버/Gotenberg 변환 — 2026-10-20 ksm 포팅).
// 이전의 html2canvas+jsPDF 클라이언트 캔버스 렌더링은 견적서와 동일한 탭 프리즈 버그 위험이
// 있어 제거함. 발주서는 저장 전(type==='add')에도 미리보기 버튼이 노출될 수 있는데, 서버
// PDF는 저장된 spoUuid가 있어야 생성 가능하므로 그 경우 안내 문구만 표시한다.
const OrderSheetPreviewModal = ({
  type,
  open,
  handleClose,
  orderSheet,
  project,
}) => {
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const prjUuid = orderSheet?.prjUuid || project?.prjUuid;
  const spoUuid = orderSheet?.spoUuid;

  useEffect(() => {
    if (!open) return;

    if (!prjUuid || !spoUuid) {
      setErrorMessage('저장 후 PDF 미리보기를 사용할 수 있습니다.');
      return;
    }

    setErrorMessage(null);
    loadPdfPreview();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prjUuid, spoUuid]);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        window.URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPdfPreview = async () => {
    setLoading(true);
    try {
      const res = await projectApi.downloadOrderSheetPdf(prjUuid, spoUuid);
      const pdfBlob = new Blob([res.data], { type: 'application/pdf' });
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      setPdfPreviewUrl(pdfUrl);
    } catch (err) {
      const message =
        typeof err.response !== 'undefined'
          ? err.response.data?.message || 'PDF 변환 서비스에 연결할 수 없습니다.'
          : err.message;
      setErrorMessage('PDF 미리보기 실패: ' + message);
    } finally {
      setLoading(false);
    }
  };

  const handlePdfDownload = () => {
    if (!pdfPreviewUrl) return;
    const link = document.createElement('a');
    link.href = pdfPreviewUrl;
    link.download = (orderSheet?.sheetCstNm || '발주서') + '.pdf';
    link.click();
  };

  const onClose = () => {
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
    handleClose();
  };

  return (
    <BootstrapDialog
      open={open}
      onClose={onClose}
      aria-labelledby="draggable-preview-dialog"
      fullWidth={true}
      maxWidth="lg"
    >
      <BootstrapDialogTitle
        style={{ cursor: 'move' }}
        id="draggable-preview-dialog"
        onClose={onClose}
      >
        발주서 미리보기
      </BootstrapDialogTitle>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DialogContent
          dividers
          sx={{
            height: '70vh',
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : pdfPreviewUrl ? (
            <iframe
              src={pdfPreviewUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="발주서 PDF 미리보기"
            />
          ) : (
            <Typography color="text.secondary" sx={{ px: 3, textAlign: 'center' }}>
              {errorMessage || 'PDF를 불러올 수 없습니다.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="warning"
            onClick={handlePdfDownload}
            disabled={!pdfPreviewUrl}
          >
            PDF 다운로드
          </Button>
        </DialogActions>
      </Box>
    </BootstrapDialog>
  );
};

export default OrderSheetPreviewModal;
